import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPhone, FaEnvelope, FaGlobe, FaMapMarkerAlt, FaWhatsapp, FaShareAlt, FaDownload, FaTags, FaBoxOpen, FaEdit, FaCheck, FaFilePdf } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// 🚀 SMART IMAGE CONVERTER
const getBase64ImageFromUrl = (imageUrl) => {
    return new Promise((resolve) => {
        if (!imageUrl) return resolve(null);
        if (imageUrl.startsWith('data:image')) return resolve(imageUrl);
        let fullUrl = imageUrl.startsWith('/') ? window.location.origin + imageUrl : 
                     !imageUrl.startsWith('http') ? window.location.origin + '/' + imageUrl : imageUrl;
        const img = new Image();
        img.crossOrigin = 'Anonymous'; 
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width; canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = () => resolve(null);
        img.src = fullUrl;
    });
};

const VisitingCard = () => {
    const [shop, setShop] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    // 🚀 EDITABLE STATE
    const [cardData, setCardData] = useState({
        shopName: '', tagline: 'Wholesale & Retail', phone1: '', phone2: '', 
        email: '', website: '', address: '', gstin: '',
        tags: 'Hardware, Plywood, Interior', products: 'Premium Plywood, Modular Kitchens, Designer Hardware'
    });

    useEffect(() => {
        const savedData = localStorage.getItem('vc_customData');
        axios.get('/api/company').then(res => {
            if (res.data) {
                const data = Array.isArray(res.data) ? res.data[0] : (res.data.data || res.data);
                setShop(data);
                if (!savedData) {
                    const phones = data.phone ? data.phone.split(/[,/|-]+/).map(p => p.trim()) : [];
                    setCardData({
                        ...cardData,
                        shopName: data.shopName || data.businessName || '',
                        phone1: phones[0] || '', phone2: phones[1] || '',
                        email: data.email || '', website: data.website ? data.website.toLowerCase() : '',
                        address: data.address || '', gstin: data.gstin ? data.gstin.toUpperCase() : ''
                    });
                } else {
                    setCardData(JSON.parse(savedData));
                }
            }
        }).catch(err => console.error("Failed to load card", err));
        // eslint-disable-next-line
    }, []);

    if (!shop) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-cyan-400 font-bold animate-pulse">Building Universal Identity...</div>;

    const toggleEdit = () => {
        if (isEditing) localStorage.setItem('vc_customData', JSON.stringify(cardData));
        setIsEditing(!isEditing);
    };

    const qrData = `MECARD:N:${cardData.shopName};TEL:${cardData.phone1};TEL:${cardData.phone2};EMAIL:${cardData.email};URL:${cardData.website};ADR:${cardData.address};;`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData)}&color=0f172a&bgcolor=ffffff&margin=1`;

    // 📸 EXPORT HD IMAGE (Auto-Height, No QR Squishing)
    const downloadImage = async () => {
        const element = document.getElementById('hidden-export-card');
        element.style.display = 'flex'; 
        await new Promise(r => setTimeout(r, 100)); 

        const canvas = await html2canvas(element, { 
            scale: 2, // HD Quality
            useCORS: true, 
            backgroundColor: '#0f172a',
            logging: false,
            width: 500
        });
        
        element.style.display = 'none'; 
        const link = document.createElement('a');
        link.download = `${cardData.shopName}_Card.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
    };

    // 📄 EXPORT REAL PDF (Zero Empty Space, Diagonal Watermark)
    const downloadRealPDF = async () => {
        // 1. 🧠 SMART HEIGHT CALCULATOR (Removes empty space)
        let estY = 40; // Top padding
        if(shop.logo) estY += 140; else estY += 120;
        
        const dummyDoc = new jsPDF('p', 'pt', [500, 1000]);
        dummyDoc.setFont("helvetica", "bold"); dummyDoc.setFontSize(26);
        const splitName = dummyDoc.splitTextToSize(cardData.shopName.toUpperCase(), 450);
        estY += (splitName.length * 32);
        estY += 80; // Tagline & Divider
        
        dummyDoc.setFontSize(14);
        const splitProds = dummyDoc.splitTextToSize(cardData.products, 420);
        estY += 25 + (splitProds.length * 20) + 40; // Tags + Prods
        
        if (cardData.phone1) estY += 40;
        if (cardData.email) estY += 40;
        if (cardData.website) estY += 40;
        if (cardData.address) {
            const splitAdd = dummyDoc.splitTextToSize(cardData.address, 360);
            estY += (splitAdd.length * 18) + 30;
        }
        if (cardData.gstin) estY += 50;
        estY += 140; // QR Code & Bottom padding

        // 2. CREATE EXACT SIZE PDF
        const doc = new jsPDF('p', 'pt', [500, estY]); 
        
        doc.setFillColor(15, 23, 42); doc.rect(0, 0, 500, estY, 'F');
        doc.setFillColor(6, 182, 212); doc.rect(0, 0, 500, 8, 'F');  

        const logoBase64 = shop.logo ? await getBase64ImageFromUrl(shop.logo) : null;
        const qrBase64 = await getBase64ImageFromUrl(qrCodeUrl);
        const footerLogoBase64 = await getBase64ImageFromUrl('/bhavya-ai-logo.png');

        // 3. 🛡️ DIAGONAL WATERMARK (Uncroppable)
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: 0.15 })); 
        if (footerLogoBase64) doc.addImage(footerLogoBase64, 'PNG', 50, (estY/2)-200, 400, 400);
        
        doc.setFont("helvetica", "bold"); doc.setFontSize(35); doc.setTextColor(6, 182, 212);
        // Angle in jsPDF rotates counter-clockwise
        doc.text("BHAVYA AI SMART BILLING", 450, 200, { angle: 45 });
        doc.setTextColor(168, 85, 247);
        doc.text("DIGITAL IDENTITY SYSTEM", 450, 450, { angle: 45 });
        doc.restoreGraphicsState();

        // 4. DRAW CONTENT
        let currentY = 50;

        if (logoBase64) {
            doc.addImage(logoBase64, 'PNG', 190, currentY, 120, 120);
            currentY += 140;
        } else {
            doc.setFont("helvetica", "bold"); doc.setFontSize(60); doc.setTextColor(6, 182, 212);
            doc.text(cardData.shopName.charAt(0) || 'C', 250, currentY + 80, { align: 'center' });
            currentY += 120;
        }

        doc.setFont("helvetica", "bold"); doc.setFontSize(26); doc.setTextColor(255, 255, 255);
        splitName.forEach(line => { doc.text(line, 250, currentY, { align: 'center' }); currentY += 32; });

        currentY += 5;
        doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(6, 182, 212);
        doc.text(cardData.tagline.toUpperCase(), 250, currentY, { align: 'center' });
        currentY += 35;

        doc.setDrawColor(30, 41, 59); doc.setLineWidth(1.5);
        doc.line(40, currentY, 460, currentY);
        currentY += 40;

        const startX = 40;
        doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(6, 182, 212);
        doc.text(cardData.tags, startX, currentY);
        currentY += 25;
        
        doc.setFont("helvetica", "normal"); doc.setFontSize(14); doc.setTextColor(203, 213, 225);
        splitProds.forEach(line => { doc.text(line, startX, currentY); currentY += 20; });
        currentY += 40;

        doc.setFontSize(15);
        const textStartX = 100;
        
        if (cardData.phone1) {
            doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255); doc.text(`Phone: `, startX, currentY);
            doc.setTextColor(74, 222, 128); 
            doc.textWithLink(cardData.phone1, textStartX, currentY, { url: `tel:${cardData.phone1}` });
            if (cardData.phone2) {
                let p1Width = doc.getTextWidth(cardData.phone1);
                doc.setTextColor(255, 255, 255); doc.text(`   |   `, textStartX + p1Width, currentY);
                let sepWidth = doc.getTextWidth(`   |   `);
                doc.setTextColor(74, 222, 128);
                doc.textWithLink(cardData.phone2, textStartX + p1Width + sepWidth, currentY, { url: `tel:${cardData.phone2}` });
            }
            currentY += 40;
        }

        if (cardData.email) {
            doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255); doc.text(`Email: `, startX, currentY);
            doc.setTextColor(56, 189, 248); doc.setFont("helvetica", "normal");
            doc.textWithLink(cardData.email, textStartX, currentY, { url: `mailto:${cardData.email}` });
            currentY += 40;
        }

        if (cardData.website) {
            doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255); doc.text(`Web: `, startX, currentY);
            doc.setTextColor(192, 132, 252); doc.setFont("helvetica", "normal");
            let webUrl = cardData.website.startsWith('http') ? cardData.website : `https://${cardData.website}`;
            doc.textWithLink(cardData.website, textStartX, currentY, { url: webUrl });
            currentY += 40;
        }

        if (cardData.address) {
            doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255); doc.text(`Add: `, startX, currentY);
            doc.setTextColor(203, 213, 225); doc.setFont("helvetica", "normal");
            const splitAdd = doc.splitTextToSize(cardData.address, 360);
            let mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cardData.shopName + ' ' + cardData.address)}`;
            splitAdd.forEach((line, i) => {
                if(i === 0) doc.textWithLink(line, textStartX, currentY, { url: mapUrl });
                else doc.text(line, textStartX, currentY);
                currentY += 20;
            });
            currentY += 30;
        }

        if (cardData.gstin) {
            doc.setFillColor(30, 41, 59); doc.roundedRect(startX, currentY - 22, 420, 40, 5, 5, 'F');
            doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(251, 191, 36);
            doc.text(`GSTIN: ${cardData.gstin.toUpperCase()}`, startX + 15, currentY + 4);
            currentY += 50;
        }

        // 5. PERFECTLY CENTERED QR CODE AT BOTTOM
        if (qrBase64) {
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(195, currentY, 110, 110, 6, 6, 'FD');
            doc.addImage(qrBase64, 'PNG', 200, currentY + 5, 100, 100);
        }

        doc.save(`${cardData.shopName}_Visiting_Card.pdf`);
    };

    const handleShare = async () => {
        const shareData = {
            title: cardData.shopName,
            text: `Connect with ${cardData.shopName}\n📱 ${cardData.phone1}\n🌐 ${cardData.website}`,
            url: window.location.href
        };
        if (navigator.share) try { await navigator.share(shareData); } catch (err) {}
        else alert("Share not supported on this browser.");
    };

    return (
        <div className="min-h-screen flex flex-col items-center py-6 px-4 font-sans bg-slate-950 relative">
            
            <div className="w-full max-w-[450px] z-10 flex flex-wrap justify-between items-center bg-slate-800/80 backdrop-blur-md p-3 rounded-2xl border border-slate-700 shadow-xl mb-6 gap-3">
                <button onClick={toggleEdit} className={`flex-1 flex justify-center items-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition-all ${isEditing ? 'bg-green-500 text-white shadow-lg shadow-green-500/30' : 'bg-slate-700 text-cyan-400 hover:bg-slate-600 border border-slate-600'}`}>
                    {isEditing ? <><FaCheck/> Save</> : <><FaEdit/> Edit</>}
                </button>
                <div className="flex gap-2">
                    <button onClick={downloadImage} className="p-3 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl shadow-lg transition" title="Save Perfect HD Image"><FaDownload /></button>
                    <button onClick={downloadRealPDF} className="p-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl shadow-lg transition" title="Save Real Vector PDF"><FaFilePdf /></button>
                    <button onClick={handleShare} className="p-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition border border-slate-600" title="Share"><FaShareAlt /></button>
                </div>
            </div>

            {/* 💳 VISIBLE UI CARD */}
            <div className="w-full max-w-[450px] z-10 pb-10">
                <div className="w-full bg-gradient-to-b from-[#0f172a] via-[#111827] to-[#020617] rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-slate-700/50 relative flex flex-col overflow-hidden p-6 pb-10">
                    <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 z-20"></div>

                    {/* ✨ DIAGONAL WATERMARK IN UI */}
                    <div className="absolute inset-0 z-0 flex items-center justify-center overflow-hidden pointer-events-none opacity-[0.04]">
                        <img src="/bhavya-ai-logo.png" alt="Logo" className="absolute w-[400px] h-[400px]" crossOrigin="anonymous"/>
                        <div className="absolute transform -rotate-45 text-4xl font-black text-cyan-400 whitespace-nowrap tracking-widest">
                            POWERED BY BHAVYA AI SMART BILLING
                        </div>
                    </div>

                    <div className="pt-8 flex flex-col items-center relative z-10 mb-8">
                        <div className="w-28 h-28 flex items-center justify-center mb-4">
                            {shop.logo ? (
                                <img src={shop.logo} alt="Logo" className="w-full h-full object-contain filter drop-shadow-[0_0_15px_rgba(255,255,255,0.15)]" crossOrigin="anonymous" />
                            ) : (
                                <span className="text-6xl font-black text-cyan-500">{cardData.shopName?.charAt(0) || 'C'}</span>
                            )}
                        </div>
                        
                        {isEditing ? (
                            <textarea className="w-full text-center bg-slate-800 border border-cyan-500 text-white font-bold text-xl outline-none resize-none rounded p-2" rows="2" value={cardData.shopName} onChange={e=>setCardData({...cardData, shopName: e.target.value})} />
                        ) : (
                            <h1 className="text-2xl font-black text-white text-center uppercase tracking-wide leading-snug">
                                {cardData.shopName}
                            </h1>
                        )}

                        {isEditing ? (
                            <input className="w-full text-center mt-3 bg-slate-800 border border-purple-500 text-cyan-400 font-bold text-sm outline-none p-2 rounded" value={cardData.tagline} onChange={e=>setCardData({...cardData, tagline: e.target.value})} />
                        ) : (
                            <h3 className="text-sm font-bold text-cyan-400 mt-2 uppercase tracking-widest text-center">{cardData.tagline}</h3>
                        )}
                    </div>

                    <div className="w-[85%] mx-auto h-[1px] bg-slate-800 mb-6 relative z-10"></div>

                    <div className="relative z-10 flex flex-col gap-6">
                        <div className="space-y-3">
                            {isEditing ? (
                                <>
                                    <input className="w-full bg-slate-800 border border-slate-600 p-2 text-white text-sm rounded" placeholder="Tags" value={cardData.tags} onChange={e=>setCardData({...cardData, tags: e.target.value})} />
                                    <textarea className="w-full bg-slate-800 border border-slate-600 p-2 text-white text-sm rounded resize-none" rows="2" placeholder="Products" value={cardData.products} onChange={e=>setCardData({...cardData, products: e.target.value})} />
                                </>
                            ) : (
                                <>
                                    <div className="flex flex-wrap justify-center gap-2 mb-2">
                                        {cardData.tags.split(',').map((t, i) => <span key={i} className="px-3 py-1.5 bg-cyan-900/30 border border-cyan-500/30 text-cyan-300 text-xs font-bold rounded-full">{t.trim()}</span>)}
                                    </div>
                                    <p className="text-slate-300 text-[15px] font-medium leading-relaxed text-center">{cardData.products}</p>
                                </>
                            )}
                        </div>

                        <div className="space-y-6 mt-4">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center text-green-400 text-lg shrink-0 mt-1"><FaPhone/></div>
                                <div className="flex-1 flex flex-col justify-center">
                                    <span className="text-[10px] text-slate-500 font-bold uppercase mb-0.5">Phone</span>
                                    {isEditing ? (
                                        <div className="flex gap-2"><input className="w-1/2 bg-slate-800 text-white text-sm p-1 rounded" value={cardData.phone1} onChange={e=>setCardData({...cardData, phone1: e.target.value})}/><input className="w-1/2 bg-slate-800 text-white text-sm p-1 rounded" value={cardData.phone2} onChange={e=>setCardData({...cardData, phone2: e.target.value})}/></div>
                                    ) : (
                                        <div className="flex flex-wrap gap-2 items-center">
                                            {cardData.phone1 && <a href={`tel:${cardData.phone1}`} className="font-bold text-white text-[15px] hover:text-green-400">{cardData.phone1}</a>}
                                            {cardData.phone2 && <span className="text-slate-600">|</span>}
                                            {cardData.phone2 && <a href={`tel:${cardData.phone2}`} className="font-bold text-white text-[15px] hover:text-green-400">{cardData.phone2}</a>}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {cardData.email || isEditing ? (
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 text-lg shrink-0"><FaEnvelope/></div>
                                    <div className="flex-1 flex flex-col justify-center overflow-hidden">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase mb-0.5">Email</span>
                                        {isEditing ? <input className="w-full bg-slate-800 text-white text-sm p-1" value={cardData.email} onChange={e=>setCardData({...cardData, email: e.target.value})}/> : <a href={`mailto:${cardData.email}`} className="text-slate-200 text-[14px]">{cardData.email}</a>}
                                    </div>
                                </div>
                            ) : null}

                            {cardData.website || isEditing ? (
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 text-lg shrink-0"><FaGlobe/></div>
                                    <div className="flex-1 flex flex-col justify-center overflow-hidden">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase mb-0.5">Website</span>
                                        {isEditing ? <input className="w-full bg-slate-800 text-white text-sm p-1" value={cardData.website} onChange={e=>setCardData({...cardData, website: e.target.value})}/> : <a href={cardData.website.startsWith('http') ? cardData.website : `https://${cardData.website}`} target="_blank" rel="noreferrer" className="text-cyan-400 font-bold text-[14px]">{cardData.website}</a>}
                                    </div>
                                </div>
                            ) : null}

                            {cardData.address || isEditing ? (
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-pink-500/10 border border-pink-500/20 flex items-center justify-center text-pink-400 text-lg shrink-0 mt-1"><FaMapMarkerAlt/></div>
                                    <div className="flex-1 flex flex-col justify-center">
                                        <span className="text-[10px] text-slate-500 font-bold uppercase mb-0.5">Location</span>
                                        {isEditing ? <textarea className="w-full bg-slate-800 text-white text-sm rounded p-1 resize-none" rows="2" value={cardData.address} onChange={e=>setCardData({...cardData, address: e.target.value})}/> : <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cardData.shopName + ' ' + cardData.address)}`} target="_blank" rel="noreferrer" className="text-slate-300 text-[13px] leading-snug">{cardData.address}</a>}
                                    </div>
                                </div>
                            ) : null}

                            {cardData.gstin || isEditing ? (
                                <div className="flex items-center gap-4 pt-2">
                                    <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 font-black text-[10px] shrink-0">GST</div>
                                    <div className="flex-1 flex flex-col justify-center">
                                        {isEditing ? <input className="w-full bg-slate-800 text-white font-mono uppercase text-sm p-1 rounded" value={cardData.gstin} onChange={e=>setCardData({...cardData, gstin: e.target.value})}/> : <span className="text-amber-400 font-mono tracking-widest font-bold uppercase text-[15px]">{cardData.gstin}</span>}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className="mt-12 flex justify-center relative z-10">
                        {/* 🛑 FLEX-NONE & STRICT SIZING TO PREVENT QR SQUISHING */}
                        <div className="w-[110px] h-[110px] shrink-0 flex-none bg-white p-2 rounded-2xl shadow-[0_0_25px_rgba(6,182,212,0.3)]">
                            <img src={qrCodeUrl} alt="QR Code" className="w-full h-full object-contain" crossOrigin="anonymous"/>
                        </div>
                    </div>
                </div>
            </div>

            {/* 🛑 HIDDEN STUDIO FOR PERFECT IMAGE EXPORT (Dynamic Height + Solid QR) */}
            <div style={{ position: 'absolute', top: '-10000px', left: '-10000px' }}>
                <div id="hidden-export-card" className="w-[500px] h-auto min-h-[850px] bg-[#0f172a] rounded-xl border border-slate-700/50 flex flex-col pt-10 pb-12 box-border relative overflow-hidden" style={{ display: 'none' }}>
                    <div className="absolute top-0 left-0 w-full h-2 bg-cyan-400 z-20"></div>

                    {/* Diagonal Watermark Export */}
                    <div className="absolute inset-0 z-0 flex items-center justify-center overflow-hidden pointer-events-none opacity-[0.04]">
                        <img src="/bhavya-ai-logo.png" alt="Watermark" className="absolute w-[450px] h-[450px]" crossOrigin="anonymous"/>
                        <div className="absolute transform -rotate-45 text-5xl font-black text-cyan-400 whitespace-nowrap tracking-widest">
                            POWERED BY BHAVYA AI SMART BILLING
                        </div>
                    </div>

                    <div className="flex flex-col items-center relative z-10 mb-6">
                        <div className="w-36 h-36 flex items-center justify-center mb-6">
                            {shop.logo ? <img src={shop.logo} alt="Logo" className="w-full h-full object-contain" crossOrigin="anonymous" /> : <span className="text-7xl font-black text-cyan-500">{cardData.shopName?.charAt(0) || 'C'}</span>}
                        </div>
                        <h1 className="text-3xl font-black text-white text-center uppercase tracking-wide leading-snug">{cardData.shopName}</h1>
                        <h3 className="text-[15px] font-bold text-cyan-400 mt-3 uppercase tracking-widest text-center">{cardData.tagline}</h3>
                    </div>

                    <div className="w-[85%] mx-auto h-[1px] bg-slate-800 mb-8 relative z-10"></div>

                    <div className="px-10 relative z-10 flex flex-col gap-8 flex-1">
                        <div className="space-y-4">
                            <div className="flex flex-wrap justify-center gap-2 mb-2">
                                {cardData.tags.split(',').map((t, i) => <span key={i} className="px-4 py-2 bg-slate-800 text-cyan-300 text-sm font-bold rounded-full">{t.trim()}</span>)}
                            </div>
                            <p className="text-slate-300 text-lg font-medium leading-relaxed text-center">{cardData.products}</p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-start gap-5">
                                <div className="w-12 h-12 rounded-full bg-green-900 border border-green-500 flex items-center justify-center text-green-400 text-xl shrink-0 mt-1"><FaPhone/></div>
                                <div className="flex-1 flex flex-col justify-center">
                                    <span className="text-xs text-slate-500 font-bold uppercase mb-1">Phone</span>
                                    <div className="flex flex-wrap gap-2 items-center">
                                        {cardData.phone1 && <span className="font-bold text-white text-lg tracking-wider">{cardData.phone1}</span>}
                                        {cardData.phone2 && <span className="text-slate-600 mx-2">|</span>}
                                        {cardData.phone2 && <span className="font-bold text-white text-lg tracking-wider">{cardData.phone2}</span>}
                                    </div>
                                </div>
                            </div>
                            {cardData.email && (
                                <div className="flex items-center gap-5">
                                    <div className="w-12 h-12 rounded-full bg-blue-900 border border-blue-500 flex items-center justify-center text-blue-400 text-xl shrink-0"><FaEnvelope/></div>
                                    <div className="flex-1 flex flex-col justify-center">
                                        <span className="text-xs text-slate-500 font-bold uppercase mb-1">Email</span>
                                        <span className="text-slate-200 text-lg">{cardData.email}</span>
                                    </div>
                                </div>
                            )}
                            {cardData.website && (
                                <div className="flex items-center gap-5">
                                    <div className="w-12 h-12 rounded-full bg-purple-900 border border-purple-500 flex items-center justify-center text-purple-400 text-xl shrink-0"><FaGlobe/></div>
                                    <div className="flex-1 flex flex-col justify-center">
                                        <span className="text-xs text-slate-500 font-bold uppercase mb-1">Website</span>
                                        <span className="text-cyan-400 font-bold text-lg">{cardData.website}</span>
                                    </div>
                                </div>
                            )}
                            {cardData.address && (
                                <div className="flex items-start gap-5">
                                    <div className="w-12 h-12 rounded-full bg-pink-900 border border-pink-500 flex items-center justify-center text-pink-400 text-xl shrink-0 mt-1"><FaMapMarkerAlt/></div>
                                    <div className="flex-1 flex flex-col justify-center">
                                        <span className="text-xs text-slate-500 font-bold uppercase mb-1">Location</span>
                                        <span className="text-slate-300 text-[16px] leading-snug">{cardData.address}</span>
                                    </div>
                                </div>
                            )}
                            {cardData.gstin && (
                                <div className="flex items-center gap-5 pt-2">
                                    <div className="w-12 h-12 rounded-full bg-amber-900 border border-amber-500 flex items-center justify-center text-amber-400 font-black text-sm shrink-0">GST</div>
                                    <div className="flex-1 flex flex-col justify-center">
                                        <span className="text-amber-400 font-mono tracking-widest font-bold uppercase text-lg">{cardData.gstin}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="mt-12 flex justify-center relative z-10">
                        {/* 🛑 PERFECT NON-SQUISHABLE QR FOR EXPORT */}
                        <div className="w-[120px] h-[120px] shrink-0 flex-none bg-white p-2 rounded-2xl">
                            <img src={qrCodeUrl} alt="QR Code" className="w-full h-full object-contain" crossOrigin="anonymous"/>
                        </div>
                    </div>

                </div>
            </div>
            
        </div>
    );
};

export default VisitingCard;